import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import AdminLayout from "../../../layouts/AdminLayout";
import {
  Button,
  Modal,
  FormField,
  TextInput,
  SelectDropdown,
  RadioGroup,
  SearchInput,
  LoadingSkeleton,
  EmptyState,
  StatusBadge,
} from "../../../components/ui";
import TablePagination from "../../../components/common/TablePagination";
import {
  getFlatById,
  getFlatsBySociety,
  getAvailableStaffForFlat,
  assignStaffToFlat,
  unassignStaffFromFlat
} from "../../../services/flatsService";
import {
  buildIndustrialTeamTree,
  getDepartmentShortCode,
  formatFloorName
} from "../../../utils/propertyStructure";
import { generateDeskCode, generateCabinCode } from "../../../utils/codeGenerator";
import styles from "./DepartmentDetail.module.css";
import {
  ArrowLeft,
  Laptop,
  Users,
  GitBranch,
  ListOrdered,
  RefreshCw,
  Phone,
  Building2,
  DoorOpen,
  Mail,
  Plus,
  Pencil,
  Trash2,
  X
} from "lucide-react";
import { toast } from "react-toastify";

const SYSTEM_ROLE_OPTIONS = [
  { value: "Head of Department (HOD)", label: "Head of Department (HOD)" },
  { value: "Team Lead (TL)", label: "Team Lead (TL)" },
  { value: "Employee", label: "Employee" },
  { value: "Industrial Director", label: "Industrial Director" },
  { value: "Plant Manager", label: "Plant Manager" },
  { value: "Gate Receptionist", label: "Gate Receptionist" },
  { value: "Industrial Receptionist", label: "Industrial Receptionist" },
  { value: "Staff", label: "Staff" },
  { value: "Security Guard", label: "Security Guard" },
];

const DUTY_STATUS_OPTIONS = [
  { value: "Active", label: "Active / On Duty" },
  { value: "Off Duty", label: "Off Duty" },
];

const DepartmentDetail = () => {
  const { unitId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const propertyIdParam = searchParams.get("propertyId") || "";
  const towerIdParam = searchParams.get("towerId") || "";

  const [unit, setUnit] = useState(null);
  const [allSocietyFlats, setAllSocietyFlats] = useState([]);
  const deptNameFromUrl = searchParams.get("dept") || "";
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("tree"); // "tree" | "desks"
  const [searchFilter, setSearchFilter] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  // Staff allocation modal state
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create"); // "create" | "edit"
  const [availableStaffList, setAvailableStaffList] = useState([]);
  const [loadingStaffList, setLoadingStaffList] = useState(false);
  const [savingAssignment, setSavingAssignment] = useState(false);

  // Modal form fields
  const [formUserId, setFormUserId] = useState("");
  const [selectedUserObj, setSelectedUserObj] = useState(null);
  const [formSystemRole, setFormSystemRole] = useState("Employee");
  const [formDesignation, setFormDesignation] = useState("");
  const [formDeskCode, setFormDeskCode] = useState("");
  const [formCabinCode, setFormCabinCode] = useState("");
  const [formReportsToId, setFormReportsToId] = useState("");
  const [formDutyStatus, setFormDutyStatus] = useState("Active");

  const fetchUnit = useCallback(async (isManualRefresh = false) => {
    if (!unitId) return;
    if (isManualRefresh && unit) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setError("");
    try {
      const data = await getFlatById(unitId);
      setUnit(data);

      const targetSocietyId = data.society_id || data.floors?.towers?.society_id || propertyIdParam;
      if (targetSocietyId) {
        try {
          const flatsData = await getFlatsBySociety(targetSocietyId);
          setAllSocietyFlats(Array.isArray(flatsData) ? flatsData : []);
        } catch (e) {
          console.error("Could not fetch full property flats for department:", e);
        }
      }
    } catch (err) {
      console.error("Failed to load department unit:", err);
      setError(err?.data?.error || err?.message || "Failed to load department details.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [unitId, unit]);

  useEffect(() => {
    fetchUnit(false);
  }, [unitId]);

  const deptName = useMemo(() => {
    if (deptNameFromUrl && deptNameFromUrl.trim()) return deptNameFromUrl.trim();

    // 1. Check assigned user's explicit department from role_details or department field
    const userWithDept = unit?.assignments?.find(
      (a) => a.user?.role_details?.department || a.user?.department
    );
    if (userWithDept) {
      const d = userWithDept.user?.role_details?.department || userWithDept.user?.department;
      if (d && String(d).trim()) return String(d).trim();
    }

    // 2. Check unit_type if not generic desk/workspace
    if (unit?.unit_type && !["desk", "cabin", "workspace", "workstation", "unit", "flat"].includes(unit.unit_type.toLowerCase())) {
      return unit.unit_type;
    }

    // 3. Parse desk code e.g. ah-dev-d01 -> "dev" -> "Development"
    const parts = String(unit?.flat_number || "").split("-");
    if (parts.length >= 3 && !/^\d+$/.test(parts[1])) {
      const token = parts[1].toLowerCase();
      const TOKEN_TO_DEPT = {
        dev: "Development",
        hr: "HR",
        mkt: "Marketing",
        it: "IT",
        ops: "Operations",
        fin: "Finance",
        acc: "Finance",
        sal: "Sales",
        dir: "Director",
        admin: "Admin",
        qa: "QA",
        civ: "Civil",
        pur: "Purchase",
        proc: "Procurement",
        rec: "Reception",
        sec: "Security",
        mngmt: "Management",
        legal: "Legal",
      };
      if (TOKEN_TO_DEPT[token]) return TOKEN_TO_DEPT[token];
      return parts[1].toUpperCase();
    }
    return unit?.unit_type || "Department";
  }, [deptNameFromUrl, unit]);

  const deptCode = useMemo(() => {
    return getDepartmentShortCode(deptName, unit?.unit_type, unit?.flat_number);
  }, [deptName, unit]);

  const departmentFlats = useMemo(() => {
    if (!allSocietyFlats.length) {
      return unit ? [unit] : [];
    }
    const normDept = deptName.toLowerCase();
    const normCode = deptCode.toLowerCase();
    const parts = String(unit?.flat_number || "").split("-");
    const deskToken = parts.length >= 3 && !/^\d+$/.test(parts[1]) ? parts[1].toLowerCase() : normCode;

    const matching = allSocietyFlats.filter((f) => {
      const uType = (f.unit_type || "").trim().toLowerCase();
      if (uType === normDept || (normCode && uType === normCode)) return true;

      const fn = (f.flat_number || "").toLowerCase();
      if (
        (normCode && (fn.startsWith(`${normCode}-`) || fn.includes(`-${normCode}-`))) ||
        (deskToken && (fn.startsWith(`${deskToken}-`) || fn.includes(`-${deskToken}-`)))
      ) {
        return true;
      }

      if (f.assignments?.some((a) => {
        const userDept = (a.user?.role_details?.department || a.user?.department || "").toLowerCase();
        return (
          userDept === normDept ||
          (normCode && userDept === normCode) ||
          (deskToken && userDept === deskToken) ||
          (normDept && userDept.includes(normDept))
        );
      })) {
        return true;
      }

      return false;
    });

    if (unit && !matching.some((f) => f.flat_id === unit.flat_id)) {
      matching.push(unit);
    }
    return matching;
  }, [allSocietyFlats, unit, deptName, deptCode]);

  const allDepartmentAssignments = useMemo(() => {
    const seenUserIds = new Set();
    const list = [];

    departmentFlats.forEach((f) => {
      const fAssignments = f.assignments || [];
      const floorNum = f.floor_number ?? (f.floors?.floor_number ?? null);
      const towerIdVal = f.tower_id ?? (f.floors?.tower_id ?? towerIdParam ?? null);

      fAssignments.forEach((a) => {
        const u = a.user;
        if (!u || seenUserIds.has(u.user_id)) return;
        seenUserIds.add(u.user_id);

        const enrichedUser = {
          ...u,
          floor_number: floorNum,
          flat_id: f.flat_id,
          desk_code: f.flat_number,
          tower_id: towerIdVal,
          role_details: {
            ...(u.role_details || {}),
            floor_number: floorNum,
            desk_code: f.flat_number,
            flat_id: f.flat_id,
            tower_id: towerIdVal,
          },
        };

        list.push({
          ...a,
          floor_number: floorNum,
          flat_number: f.flat_number,
          flat_id: f.flat_id,
          tower_id: towerIdVal,
          user: enrichedUser,
        });
      });
    });

    (unit?.assignments || []).forEach((a) => {
      const u = a.user;
      if (u && !seenUserIds.has(u.user_id)) {
        seenUserIds.add(u.user_id);
        const floorNum = unit.floor_number ?? (unit.floors?.floor_number ?? null);
        const towerIdVal = unit.tower_id ?? (unit.floors?.tower_id ?? towerIdParam ?? null);

        const enrichedUser = {
          ...u,
          floor_number: floorNum,
          flat_id: unit.flat_id,
          desk_code: unit.flat_number,
          tower_id: towerIdVal,
          role_details: {
            ...(u.role_details || {}),
            floor_number: floorNum,
            desk_code: unit.flat_number,
            flat_id: unit.flat_id,
            tower_id: towerIdVal,
          },
        };

        list.push({
          ...a,
          floor_number: floorNum,
          flat_number: unit.flat_number,
          flat_id: unit.flat_id,
          tower_id: towerIdVal,
          user: enrichedUser,
        });
      }
    });

    return list;
  }, [departmentFlats, unit, towerIdParam]);

  const { rootNodes, allMembers, totalMembers, totalDesks } = useMemo(() => {
    return buildIndustrialTeamTree(allDepartmentAssignments);
  }, [allDepartmentAssignments]);

  const distinctFloors = useMemo(() => {
    const floorsSet = new Set();
    departmentFlats.forEach(f => {
      const fn = f.floor_number ?? f.floors?.floor_number;
      if (fn != null) floorsSet.add(fn);
    });
    if (unit?.floor_number != null) floorsSet.add(unit.floor_number);
    return Array.from(floorsSet).sort((a, b) => a - b);
  }, [departmentFlats, unit]);

  const staffOptions = useMemo(() => {
    return availableStaffList.map((u) => {
      let extra = "";
      if (u.is_assigned_here) extra = " • [Currently In This Dept]";
      else if (u.other_assignments && u.other_assignments.length > 0)
        extra = ` • [In ${u.other_assignments.join(", ")}]`;
      return {
        value: String(u.user_id),
        label: `${u.full_name} (${u.system_role || u.primary_role || "Staff"})${extra}`,
      };
    });
  }, [availableStaffList]);

  const reportsToOptions = useMemo(() => {
    const opts = [
      { value: "", label: "-- Direct Department Head / Top Level --" },
    ];
    allMembers
      .filter((m) => String(m.user_id) !== String(formUserId))
      .forEach((m) => {
        opts.push({
          value: String(m.user_id),
          label: `${m.full_name} (${m.system_role}${m.designation ? ` - ${m.designation}` : ""})`,
        });
      });
    return opts;
  }, [allMembers, formUserId]);

  const societyId = unit?.society_id || propertyIdParam;
  const towerId = unit?.tower_id || towerIdParam;
  const societyName = unit?.society_name || "AsmitA House";
  const towerName = unit?.tower_name || "Main Wing";

  const handleBack = () => {
    if (societyId && towerId) {
      navigate(`/properties?propertyId=${societyId}&towerId=${towerId}`);
    } else if (societyId) {
      navigate(`/properties?propertyId=${societyId}`);
    } else {
      navigate(`/properties`);
    }
  };

  const loadAvailableStaff = useCallback(async () => {
    if (!unitId) return;
    try {
      setLoadingStaffList(true);
      const res = await getAvailableStaffForFlat(unitId);
      if (res?.users) {
        setAvailableStaffList(res.users);
      }
    } catch (e) {
      console.error("Could not fetch available staff:", e);
    } finally {
      setLoadingStaffList(false);
    }
  }, [unitId]);

  const handleRegenerateDeskCode = () => {
    const existingDeskCodes = allMembers
      .map((m) => m.desk_code)
      .filter(Boolean)
      .concat(formDeskCode ? [formDeskCode] : []);
    const nextDesk = generateDeskCode(deptCode, existingDeskCodes);
    setFormDeskCode(nextDesk);
    toast.info(`Regenerated Desk Code: ${nextDesk}`);
  };

  const handleRegenerateCabinCode = () => {
    const existingCabinCodes = allMembers
      .map((m) => m.cabin_code)
      .filter(Boolean)
      .concat(formCabinCode ? [formCabinCode] : []);
    const nextCabin = generateCabinCode(deptCode, existingCabinCodes);
    setFormCabinCode(nextCabin);
    toast.info(`Regenerated Cabin Code: ${nextCabin}`);
  };

  const handleClearCabinCode = () => {
    setFormCabinCode("");
  };

  const handleOpenAssignModal = (memberToEdit = null) => {
    loadAvailableStaff();
    const existingDeskCodes = allMembers.map((m) => m.desk_code).filter(Boolean);
    if (memberToEdit) {
      setModalMode("edit");
      setFormUserId(String(memberToEdit.user_id));
      setSelectedUserObj(memberToEdit);
      setFormSystemRole(memberToEdit.system_role || "Employee");
      setFormDesignation(memberToEdit.designation || "");
      setFormDeskCode(memberToEdit.desk_code || generateDeskCode(deptCode, existingDeskCodes));
      setFormCabinCode(memberToEdit.cabin_code || "");
      setFormReportsToId(memberToEdit.reports_to_id ? String(memberToEdit.reports_to_id) : "");
      setFormDutyStatus(memberToEdit.duty_status || "Active");
    } else {
      setModalMode("create");
      setFormUserId("");
      setSelectedUserObj(null);
      setFormSystemRole("Employee");
      setFormDesignation("");
      const suggestedDesk = generateDeskCode(deptCode, existingDeskCodes);
      setFormDeskCode(suggestedDesk);
      setFormCabinCode("");
      const lead = allMembers.find((m) => m.is_lead || String(m.system_role).toLowerCase().includes("head") || String(m.system_role).toLowerCase().includes("lead"));
      setFormReportsToId(lead ? String(lead.user_id) : "");
      setFormDutyStatus("Active");
    }
    setIsAssignModalOpen(true);
  };

  const handleSelectUser = (userId) => {
    setFormUserId(userId);
    const user = availableStaffList.find((u) => String(u.user_id) === String(userId));
    if (user) {
      setSelectedUserObj(user);
      if (user.system_role) setFormSystemRole(user.system_role);
      if (user.designation) setFormDesignation(user.designation);
      if (user.desk_code) {
        setFormDeskCode(user.desk_code);
      } else if (!formDeskCode) {
        const existingDeskCodes = allMembers.map((m) => m.desk_code).filter(Boolean);
        setFormDeskCode(generateDeskCode(deptCode, existingDeskCodes));
      }
      if (user.cabin_code) setFormCabinCode(user.cabin_code);
      if (user.reports_to_id && !formReportsToId) setFormReportsToId(String(user.reports_to_id));
      if (user.duty_status) setFormDutyStatus(user.duty_status);
    } else {
      setSelectedUserObj(null);
    }
  };

  const handleSubmitAssignment = async (e) => {
    e?.preventDefault();
    if (!formUserId) {
      toast.error("Please select an employee to allocate.");
      return;
    }

    try {
      setSavingAssignment(true);
      await assignStaffToFlat(unitId, {
        user_id: Number(formUserId),
        system_role: formSystemRole,
        designation: formDesignation.trim(),
        desk_code: formDeskCode.trim().toUpperCase(),
        cabin_code: formCabinCode.trim().toUpperCase(),
        reports_to_id: formReportsToId ? Number(formReportsToId) : null,
        duty_status: formDutyStatus,
      });

      setIsAssignModalOpen(false);
      await fetchUnit(true);
    } catch (err) {
      console.error(err);
    } finally {
      setSavingAssignment(false);
    }
  };

  const handleUnassign = async (member) => {
    const confirmed = window.confirm(
      `Are you sure you want to unassign ${member.full_name} from ${unit?.flat_number || "this department"}? Their workstation code (${member.desk_code || "Unassigned"}) will be cleared.`
    );
    if (!confirmed) return;

    try {
      await unassignStaffFromFlat(unitId, member.user_id);
      await fetchUnit(true);
    } catch (err) {
      console.error(err);
    }
  };

  // Filtered members for workstation directory
  const filteredMembers = useMemo(() => {
    if (!searchFilter.trim()) return allMembers;
    const q = searchFilter.trim().toLowerCase();
    return allMembers.filter((m) => {
      return (
        String(m.full_name || "").toLowerCase().includes(q) ||
        String(m.desk_code || "").toLowerCase().includes(q) ||
        String(m.designation || "").toLowerCase().includes(q) ||
        String(m.system_role || "").toLowerCase().includes(q) ||
        String(m.reports_to_name || "").toLowerCase().includes(q)
      );
    });
  }, [allMembers, searchFilter]);

  const paginatedMembers = filteredMembers.slice((page - 1) * limit, page * limit);

  // Tree rendering
  const renderTreeNode = (node, isRoot = false, isLead = false) => {
    const initials = (node.full_name || "S")
      .split(" ")
      .map((n) => n[0])
      .slice(0, 2)
      .join("")
      .toUpperCase();

    const hasChildren = Array.isArray(node.children) && node.children.length > 0;
    const isHighlighted = searchFilter.trim() && (
      String(node.full_name || "").toLowerCase().includes(searchFilter.trim().toLowerCase()) ||
      String(node.desk_code || "").toLowerCase().includes(searchFilter.trim().toLowerCase()) ||
      String(node.designation || "").toLowerCase().includes(searchFilter.trim().toLowerCase())
    );

    return (
      <div
        key={node.user_id}
        className={isRoot ? styles.rootBranch : styles.subBranch}
      >
        <div
          className={[
            styles.nodeCard,
            isRoot ? styles.nodeCardRoot : "",
            isLead && !isRoot ? styles.nodeCardLead : "",
            isHighlighted ? styles.nodeCardHighlight : ""
          ].filter(Boolean).join(" ")}
        >
          <div className={styles.nodeTop}>
            <div className={styles.nodeAvatar}>{initials}</div>
            <div className={styles.nodeInfo}>
              <div className={styles.nodeName}>{node.full_name}</div>
              <span
                className={[
                  styles.nodeRole,
                  isRoot ? styles.nodeRoleRoot : "",
                  isLead && !isRoot ? styles.nodeRoleLead : "",
                ].filter(Boolean).join(" ")}
              >
                {node.system_role || "Staff"}
              </span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              style={{ width: "24px", height: "24px", minWidth: "24px", padding: 0, marginLeft: "auto", flexShrink: 0 }}
              onClick={(e) => {
                e.stopPropagation();
                handleOpenAssignModal(node);
              }}
              title="Edit Staff & Desk"
              icon={<Pencil size={11} />}
            />
          </div>

          {/* Designation - Strictly Highlighted as a Data Field */}
          <div className={styles.designationBox}>
            <span className={styles.designationLabel}>Designation (Data Field)</span>
            <div className={styles.designationVal}>
              {node.designation || "Not Assigned"}
            </div>
          </div>

          {/* Floor, Workstation & Cabin Data with Links to Floor and Desk in Property Explorer */}
          <div style={{ display: "flex", flexDirection: "column", gap: "6px", margin: "8px 0" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{ fontSize: "11px", fontWeight: 700, color: "#64748b", textTransform: "uppercase" }}>
                Floor:
              </span>
              {node.floor_number != null ? (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/properties?propertyId=${societyId}&towerId=${node.tower_id || towerId}&floor=${node.floor_number}`);
                  }}
                  title={`Navigate to Floor ${node.floor_number} in Property Explorer`}
                  style={{
                    fontSize: "11px",
                    fontWeight: 700,
                    color: "#2563eb",
                    background: "#eff6ff",
                    padding: "2px 8px",
                    borderRadius: "6px",
                    border: "1px solid #bfdbfe",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "4px",
                    cursor: "pointer",
                  }}
                >
                  <Building2 size={11} /> Floor {node.floor_number}
                </button>
              ) : (
                <span className={styles.deskCodeEmpty}>Unassigned floor</span>
              )}
            </div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{ fontSize: "11px", fontWeight: 700, color: "#64748b", textTransform: "uppercase" }}>
                Workstation / Desk:
              </span>
              {node.desk_code ? (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/properties?propertyId=${societyId}&towerId=${node.tower_id || towerId}&floor=${node.floor_number != null ? node.floor_number : ""}&desk=${node.desk_code}`);
                  }}
                  title={`View ${node.desk_code} in Property Explorer`}
                  className={styles.deskCodeBadge}
                  style={{ cursor: "pointer", border: "none" }}
                >
                  <Laptop size={12} />
                  {node.desk_code}
                </button>
              ) : (
                <span className={styles.deskCodeEmpty}>No desk code</span>
              )}
            </div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{ fontSize: "11px", fontWeight: 700, color: "#64748b", textTransform: "uppercase" }}>
                Cabin:
              </span>
              {node.cabin_code ? (
                <span style={{ fontSize: "11px", fontWeight: 700, color: "#0d9488", background: "#f0fdfa", padding: "2px 8px", borderRadius: "6px", border: "1px solid #ccfbf1", display: "inline-flex", alignItems: "center", gap: "4px" }}>
                  <DoorOpen size={12} /> {node.cabin_code}
                </span>
              ) : (
                <span className={styles.deskCodeEmpty}>No cabin</span>
              )}
            </div>
          </div>

          <div className={styles.nodeFooter}>
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <span style={{
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                backgroundColor: node.duty_status === "Off Duty" ? "#94a3b8" : "#22c55e",
                display: "inline-block"
              }} />
              <span style={{ fontWeight: 600 }}>{node.duty_status || "Active"}</span>
            </div>
            {node.mobile_number && (
              <span style={{ fontSize: "11.5px", color: "#64748b", display: "inline-flex", alignItems: "center", gap: "4px" }}>
                <Phone size={11} /> {node.mobile_number}
              </span>
            )}
          </div>
        </div>

        {hasChildren && (
          <>
            <div className={styles.stemDown} />
            <div className={styles.branchRow}>
              {node.children.map((child) => renderTreeNode(child, false, child.is_lead))}
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <AdminLayout title="Department Operations & Team">
      <div className={styles.pageContainer}>
        {/* Navigation & Breadcrumb Bar */}
        <div className={styles.breadcrumbBar}>
          <Button variant="outline" size="sm" onClick={handleBack} icon={<ArrowLeft size={16} />}>
            Back to Departments & Desks in {towerName}
          </Button>

          <div className={styles.breadcrumbTrail}>
            <span className={styles.breadcrumbItem} onClick={() => navigate("/properties")}>Properties</span>
            <span>/</span>
            <span className={styles.breadcrumbItem} onClick={() => navigate(`/properties?propertyId=${societyId}`)}>{societyName}</span>
            <span>/</span>
            <span className={styles.breadcrumbItem} onClick={handleBack}>{towerName}</span>
            <span>/</span>
            <span className={styles.breadcrumbActive}>
              {deptName ? `${deptName} Department` : (unit?.flat_number || (loading ? "Loading..." : "Department"))}
            </span>
          </div>
        </div>

        {loading && !unit ? (
          <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
            {/* Header Banner Skeleton */}
            <div className={styles.headerBanner} style={{ pointerEvents: "none" }}>
              <div className={styles.bannerLeft}>
                <div className={styles.deptSkeletonShimmer} style={{ width: "56px", height: "56px", borderRadius: "14px", flexShrink: 0 }} />
                <div className={styles.deptTitleInfo} style={{ gap: "8px" }}>
                  <div className={styles.deptSkeletonShimmer} style={{ width: "220px", height: "24px", borderRadius: "6px" }} />
                  <div className={styles.deptMetaRow}>
                    <div className={styles.deptSkeletonShimmer} style={{ width: "70px", height: "20px", borderRadius: "6px" }} />
                    <div className={styles.deptSkeletonShimmer} style={{ width: "140px", height: "20px", borderRadius: "20px" }} />
                    <div className={styles.deptSkeletonShimmer} style={{ width: "80px", height: "18px", borderRadius: "4px" }} />
                  </div>
                </div>
              </div>

              <div className={styles.bannerStats}>
                <div className={styles.statBox} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "6px" }}>
                  <div className={styles.deptSkeletonShimmer} style={{ width: "36px", height: "22px", borderRadius: "4px" }} />
                  <div className={styles.deptSkeletonShimmer} style={{ width: "65px", height: "10px", borderRadius: "3px" }} />
                </div>
                <div className={styles.statBox} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "6px" }}>
                  <div className={styles.deptSkeletonShimmer} style={{ width: "36px", height: "22px", borderRadius: "4px" }} />
                  <div className={styles.deptSkeletonShimmer} style={{ width: "60px", height: "10px", borderRadius: "3px" }} />
                </div>
              </div>
            </div>

            {/* Controls Bar Skeleton */}
            <div className={styles.controlBar} style={{ pointerEvents: "none" }}>
              <div className={styles.deptSkeletonShimmer} style={{ width: "340px", height: "40px", borderRadius: "10px" }} />
              <div className={styles.toolbarActions} style={{ flex: 1, justifyContent: "flex-end", gap: "12px" }}>
                <div className={styles.deptSkeletonShimmer} style={{ width: "140px", height: "38px", borderRadius: "8px" }} />
                <div className={styles.deptSkeletonShimmer} style={{ width: "220px", height: "38px", borderRadius: "8px" }} />
              </div>
            </div>

            {/* Tree Canvas Hierarchy Skeleton */}
            <div className={styles.treeCanvas} style={{ pointerEvents: "none", minHeight: "340px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "32px", padding: "40px 20px" }}>
              {/* Root Card Skeleton */}
              <div className={[styles.nodeCard, styles.nodeCardRoot].join(" ")} style={{ width: "310px" }}>
                <div className={styles.nodeTop}>
                  <div className={styles.deptSkeletonShimmer} style={{ width: "44px", height: "44px", borderRadius: "12px", flexShrink: 0 }} />
                  <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "6px" }}>
                    <div className={styles.deptSkeletonShimmer} style={{ width: "130px", height: "16px", borderRadius: "4px" }} />
                    <div className={styles.deptSkeletonShimmer} style={{ width: "80px", height: "12px", borderRadius: "4px" }} />
                  </div>
                </div>
                <div style={{ marginTop: "12px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div className={styles.deptSkeletonShimmer} style={{ width: "85px", height: "22px", borderRadius: "6px" }} />
                  <div className={styles.deptSkeletonShimmer} style={{ width: "55px", height: "18px", borderRadius: "10px" }} />
                </div>
              </div>

              {/* Child Nodes Row Skeleton */}
              <div style={{ display: "flex", gap: "32px", justifyContent: "center", flexWrap: "wrap", width: "100%" }}>
                {Array.from({ length: 2 }).map((_, cIdx) => (
                  <div key={cIdx} className={styles.nodeCard} style={{ width: "290px" }}>
                    <div className={styles.nodeTop}>
                      <div className={styles.deptSkeletonShimmer} style={{ width: "40px", height: "40px", borderRadius: "12px", flexShrink: 0 }} />
                      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "6px" }}>
                        <div className={styles.deptSkeletonShimmer} style={{ width: "110px", height: "15px", borderRadius: "4px" }} />
                        <div className={styles.deptSkeletonShimmer} style={{ width: "70px", height: "11px", borderRadius: "4px" }} />
                      </div>
                    </div>
                    <div style={{ marginTop: "10px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div className={styles.deptSkeletonShimmer} style={{ width: "75px", height: "20px", borderRadius: "6px" }} />
                      <div className={styles.deptSkeletonShimmer} style={{ width: "45px", height: "16px", borderRadius: "10px" }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : error || !unit ? (
          <div className={styles.notFoundCard}>
            <div className={styles.notFoundIconCircle}>
              <Building2 size={36} color="#64748b" />
            </div>
            <h2 className={styles.notFoundTitle}>Department Not Found</h2>
            <p className={styles.notFoundDesc}>
              {error || `Department unit #${unitId} could not be located in ${towerName}. It may have been reorganized.`}
            </p>
            <div className={styles.notFoundActions}>
              <Button variant="primary" onClick={handleBack} icon={<ArrowLeft size={16} />}>
                Return to {towerName}
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate(`/properties?propertyId=${societyId}`)}
              >
                View All Property Facilities & Departments
              </Button>
            </div>
          </div>
        ) : (
          <>
            {/* Executive Hero Banner */}
            <div className={styles.headerBanner}>
              {refreshing && <div className={styles.refreshProgressBar} />}
              <div className={styles.bannerLeft}>
                <div className={styles.deptIconCircle}>
                  {deptCode}
                </div>
                <div className={styles.deptTitleInfo}>
                  <h1 className={styles.deptTitle}>{deptName}</h1>
                  <div className={styles.deptMetaRow}>
                    <span className={styles.deptCodeBadge}>DEPT: {deptCode}</span>
                    <span className={styles.deptFloorBadge}>
                      {distinctFloors.length > 0 ? `${distinctFloors.map(f => formatFloorName(f)).join(", ")} • ${towerName} (${societyName})` : `${formatFloorName(unit.floor_number)} • ${towerName} (${societyName})`}
                    </span>
                    <span className={styles.deptTypeBadge}>
                      {unit.unit_type || "Department"}
                    </span>
                  </div>
                </div>
              </div>

              <div className={styles.bannerStats}>
                <div className={styles.statBox}>
                  <div className={styles.statVal}>{totalMembers}</div>
                  <div className={styles.statLbl}>Staff Members</div>
                </div>
                <div className={styles.statBox}>
                  <div className={styles.statVal}>{totalDesks}</div>
                  <div className={styles.statLbl}>Desk Codes</div>
                </div>
              </div>
            </div>

            {/* Controls Bar */}
            <div className={styles.controlBar}>
              <div style={{ minWidth: "360px" }}>
                <RadioGroup
                  name="departmentTab"
                  value={activeTab}
                  onChange={(e) => {
                    setActiveTab(e.target.value);
                    setPage(1);
                  }}
                  options={[
                    { value: "tree", label: "Team Hierarchy Tree", icon: <GitBranch size={15} /> },
                    { value: "desks", label: `Workstations & Desks (${totalDesks})`, icon: <ListOrdered size={15} /> },
                  ]}
                  variant="segmented"
                />
              </div>

              <div className={styles.toolbarActions}>
                <Button
                  variant="primary"
                  onClick={() => handleOpenAssignModal()}
                  icon={<Plus size={15} />}
                >
                  Assign Staff / Desk
                </Button>
                <div style={{ minWidth: "220px", flex: 1 }}>
                  <SearchInput
                    placeholder={activeTab === "tree" ? "Highlight in tree..." : "Filter desk code / staff..."}
                    value={searchFilter}
                    onChange={(val) => {
                      setSearchFilter(val);
                      setPage(1);
                    }}
                  />
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchUnit(true)}
                  title="Refresh Data"
                  loading={refreshing}
                  icon={<RefreshCw size={14} className={refreshing ? styles.spin : ""} />}
                />
              </div>
            </div>

            {/* Tab 1: Interactive Hierarchy Tree */}
            {activeTab === "tree" && (
              <div className={styles.treeCanvas}>
                {rootNodes.length > 0 ? (
                  <div className={styles.treeRootContainer}>
                    {rootNodes.map((root, idx) => (
                      <div key={root.user_id} style={{ marginBottom: idx < rootNodes.length - 1 ? "48px" : "0", width: "100%", display: "flex", justifyContent: "center" }}>
                        {renderTreeNode(root, true, root.is_lead)}
                      </div>
                    ))}
                  </div>
                ) : (
                  <EmptyState
                    icon={<Users size={56} />}
                    title="No Team Members Assigned"
                    description="Assign department employees to view the interactive team tree."
                    action={
                      <Button
                        variant="primary"
                        onClick={() => handleOpenAssignModal()}
                        icon={<Plus size={15} />}
                      >
                        Assign Department Employee
                      </Button>
                    }
                  />
                )}
              </div>
            )}

            {/* Tab 2: Workstations & Desk Codes Roster Directory */}
            {activeTab === "desks" && (
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th className={styles.th}>Desk / Cabin</th>
                      <th className={styles.th}>Floor</th>
                      <th className={styles.th}>Employee Name & Contact</th>
                      <th className={styles.th}>Designation (Data Field)</th>
                      <th className={styles.th}>System Role</th>
                      <th className={styles.th}>Reports To</th>
                      <th className={styles.th}>Status</th>
                      <th className={styles.th} style={{ textAlign: "right" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMembers.length > 0 ? (
                      paginatedMembers.map((m) => (
                        <tr key={m.user_id} className={styles.tr}>
                          <td className={styles.td}>
                            {m.desk_code ? (
                              <button
                                type="button"
                                onClick={() => navigate(`/properties?propertyId=${societyId}&towerId=${m.tower_id || towerId}&floor=${m.floor_number != null ? m.floor_number : ""}&desk=${m.desk_code}`)}
                                className={styles.deskCodeBadge}
                                style={{ cursor: "pointer", border: "none" }}
                              >
                                <Laptop size={13} />
                                {m.desk_code}
                              </button>
                            ) : (
                              <span className={styles.deskCodeEmpty}>Unassigned</span>
                            )}
                            {m.cabin_code && (
                              <div style={{ marginTop: "4px", fontSize: "11px", fontWeight: 700, color: "#0d9488", display: "inline-flex", alignItems: "center", gap: "4px" }}>
                                <DoorOpen size={12} /> {m.cabin_code}
                              </div>
                            )}
                          </td>
                          <td className={styles.td}>
                            {m.floor_number != null ? (
                              <button
                                type="button"
                                onClick={() => navigate(`/properties?propertyId=${societyId}&towerId=${m.tower_id || towerId}&floor=${m.floor_number}`)}
                                style={{
                                  fontSize: "11px",
                                  fontWeight: 700,
                                  color: "#2563eb",
                                  background: "#eff6ff",
                                  padding: "3px 8px",
                                  borderRadius: "6px",
                                  border: "1px solid #bfdbfe",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: "4px",
                                  cursor: "pointer",
                                }}
                              >
                                <Building2 size={11} /> Floor {m.floor_number}
                              </button>
                            ) : (
                              <span style={{ color: "#94a3b8", fontSize: "12px" }}>-</span>
                            )}
                          </td>
                          <td className={styles.td}>
                            <div style={{ fontWeight: 800, color: "#0f172a", fontSize: "14px" }}>
                              {m.full_name}
                            </div>
                            <div className={styles.contactInfo}>
                              {m.email_id && <span style={{ marginRight: "10px", display: "inline-flex", alignItems: "center", gap: "4px" }}><Mail size={12} /> {m.email_id}</span>}
                              {m.mobile_number && <span style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}><Phone size={12} /> {m.mobile_number}</span>}
                            </div>
                          </td>
                          <td className={styles.td}>
                            <span style={{
                              fontWeight: 700,
                              color: "#1e293b",
                              background: "#f1f5f9",
                              padding: "4px 10px",
                              borderRadius: "6px",
                              display: "inline-block",
                              border: "1px solid #e2e8f0"
                            }}>
                              {m.designation || "-"}
                            </span>
                          </td>
                          <td className={styles.td}>
                            <span style={{
                              fontSize: "11px",
                              fontWeight: 800,
                              textTransform: "uppercase",
                              padding: "3px 8px",
                              borderRadius: "4px",
                              background: m.is_lead ? "#ede9fe" : "#f1f5f9",
                              color: m.is_lead ? "#6d28d9" : "#475569"
                            }}>
                              {m.system_role || "Staff"}
                            </span>
                          </td>
                          <td className={styles.td}>
                            {m.reports_to_name ? (
                              <span style={{ fontWeight: 600, color: "#334155" }}>
                                {m.reports_to_name}
                              </span>
                            ) : (
                              <span style={{ color: "#94a3b8", fontSize: "12px" }}>
                                {m.is_lead ? "Department Head / Director" : "-"}
                              </span>
                            )}
                          </td>
                          <td className={styles.td}>
                            <StatusBadge
                              variant={m.duty_status === "Off Duty" ? "neutral" : "success"}
                              label={m.duty_status || "Active"}
                            />
                          </td>
                          <td className={styles.td} style={{ textAlign: "right" }}>
                            <div style={{ display: "inline-flex", gap: "6px", justifyContent: "flex-end" }}>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleOpenAssignModal(m)}
                                title="Edit Workstation & Role"
                                icon={<Pencil size={13} />}
                              />
                              <Button
                                variant="danger"
                                size="sm"
                                onClick={() => handleUnassign(m)}
                                title="Unassign from Department"
                                icon={<Trash2 size={13} />}
                              />
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={7}>
                          <EmptyState
                            icon={<Users size={36} />}
                            title="No staff records found"
                            description={
                              searchFilter
                                ? `No workstation or staff records found matching "${searchFilter}".`
                                : "No workstations or staff assigned yet."
                            }
                            action={
                              <Button
                                variant="primary"
                                size="sm"
                                icon={<Plus size={15} />}
                                onClick={() => handleOpenAssignModal()}
                              >
                                Allocate Workstation / Staff
                              </Button>
                            }
                          />
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
                <TablePagination
                  totalItems={filteredMembers.length}
                  page={page}
                  limit={limit}
                  onPageChange={setPage}
                  onLimitChange={setLimit}
                />
              </div>
            )}
          </>
        )}

        {/* Assign / Edit Staff & Workstation Modal */}
        <Modal
          isOpen={isAssignModalOpen}
          onClose={() => setIsAssignModalOpen(false)}
          title={
            modalMode === "edit"
              ? `Edit Staff & Workstation Details`
              : `Assign Staff Member to ${unit?.flat_number || "Department"}`
          }
          size="md"
        >
          <form onSubmit={handleSubmitAssignment} className={styles.modalForm}>
            <div className={styles.modalDepartmentBanner}>
              <span className={styles.modalDeptBadge}>DEPT: {deptCode}</span>
              <div className={styles.modalDeptInfo}>
                <strong>{unit?.flat_number}</strong> • {unit?.unit_type || "Department"} • {towerName} ({societyName})
              </div>
            </div>

            {/* Employee Selection */}
            {modalMode === "create" ? (
              <FormField
                label="Select Employee / Staff Member"
                required
                hint={`Select an existing staff member belonging to ${societyName}.`}
              >
                <SelectDropdown
                  value={formUserId}
                  onChange={(e) => handleSelectUser(e.target.value)}
                  options={staffOptions}
                  placeholder={loadingStaffList ? "Loading Property Staff Members..." : "Choose an Employee from Property Roster"}
                  disabled={loadingStaffList}
                  isSearchable
                />
              </FormField>
            ) : (
              <FormField
                label="Assigned Employee"
                hint={`Selected staff member for ${unit?.flat_number || "Department"}.`}
              >
                <div style={{ padding: "10px 14px", background: "#f8fafc", border: "1px solid #cbd5e1", borderRadius: "8px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <div style={{ fontWeight: 800, color: "#0f172a", fontSize: "14px" }}>{selectedUserObj?.full_name}</div>
                    <div style={{ fontSize: "12px", color: "#64748b" }}>{selectedUserObj?.email_id || selectedUserObj?.mobile_number || "Staff Member"}</div>
                  </div>
                  <span style={{ fontSize: "11px", fontWeight: 700, padding: "2px 8px", borderRadius: "4px", background: "#eff6ff", color: "var(--color-dark-bg)" }}>
                    ID: #{selectedUserObj?.user_id}
                  </span>
                </div>
              </FormField>
            )}

            <div className={styles.formGrid}>
              {/* System Role */}
              <FormField
                label="System Role"
                required
                hint="Determines system operational permissions"
              >
                <SelectDropdown
                  value={formSystemRole}
                  onChange={(e) => setFormSystemRole(e.target.value)}
                  options={SYSTEM_ROLE_OPTIONS}
                  isSearchable={false}
                  isClearable={false}
                />
              </FormField>

              {/* Designation */}
              <FormField
                label="Designation (Data Field)"
                hint="Data field for organizational title"
              >
                <TextInput
                  placeholder="e.g. Lead Analyst, Senior Executive"
                  value={formDesignation}
                  onChange={(e) => setFormDesignation(e.target.value)}
                />
              </FormField>
            </div>

            <div className={styles.formGrid}>
              {/* Desk / Workstation Code */}
              <FormField
                label="Desk / Workstation Code (Auto-Generated)"
                hint="Auto-generated hardware / seat allocation code (read-only)"
              >
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <TextInput
                    readOnly
                    placeholder={`${deptCode}-WS-01`}
                    value={formDeskCode}
                    style={{ background: '#f8fafc', fontFamily: 'monospace', fontWeight: 700, cursor: 'not-allowed' }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleRegenerateDeskCode}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', whiteSpace: 'nowrap', height: '40px', padding: '0 12px' }}
                    title="Regenerate Workstation Code"
                  >
                    <RefreshCw size={13} /> Regenerate
                  </Button>
                </div>
              </FormField>

              {/* Cabin Code */}
              <FormField
                label="Cabin Code (Optional)"
                hint="Auto-generated private cabin code (read-only)"
              >
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <TextInput
                    readOnly
                    placeholder="No Cabin Assigned"
                    value={formCabinCode}
                    style={{ background: '#f8fafc', fontFamily: 'monospace', fontWeight: 700, cursor: 'not-allowed' }}
                  />
                  {formCabinCode ? (
                    <>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleRegenerateCabinCode}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', whiteSpace: 'nowrap', height: '40px', padding: '0 12px' }}
                        title="Regenerate Cabin Code"
                      >
                        <RefreshCw size={13} /> Regenerate
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={handleClearCabinCode}
                        style={{ display: 'inline-flex', alignItems: 'center', padding: '0 8px', height: '40px', color: '#dc2626' }}
                        title="Clear Cabin"
                      >
                        <X size={14} />
                      </Button>
                    </>
                  ) : (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleRegenerateCabinCode}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', whiteSpace: 'nowrap', height: '40px', padding: '0 12px' }}
                      title="Assign Auto-Generated Cabin"
                    >
                      <Plus size={13} /> Assign Cabin
                    </Button>
                  )}
                </div>
              </FormField>
            </div>

            <div className={styles.formGrid}>
              {/* Reports To (Hierarchy parent) */}
              <FormField
                label="Reports To (Hierarchy)"
                hint="Connects tree hierarchy branches"
              >
                <SelectDropdown
                  value={formReportsToId}
                  onChange={(e) => setFormReportsToId(e.target.value)}
                  options={reportsToOptions}
                  placeholder="-- Direct Department Head / Top Level --"
                  isSearchable
                  isClearable
                />
              </FormField>

              {/* Duty Status */}
              <FormField
                label="Duty Status"
                hint="Current operational shift status"
              >
                <SelectDropdown
                  value={formDutyStatus}
                  onChange={(e) => setFormDutyStatus(e.target.value)}
                  options={DUTY_STATUS_OPTIONS}
                  isSearchable={false}
                  isClearable={false}
                />
              </FormField>
            </div>

            <div className={styles.modalFooter}>
              <Button
                variant="outline"
                onClick={() => setIsAssignModalOpen(false)}
                disabled={savingAssignment}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                loading={savingAssignment}
                disabled={savingAssignment || !formUserId}
              >
                {modalMode === "edit" ? "Save Changes" : "Assign to Department"}
              </Button>
            </div>
          </form>
        </Modal>
      </div>
    </AdminLayout>
  );
};

export default DepartmentDetail;
